import React, { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { EyeInvisibleOutlined, EyeOutlined, CheckCircleFilled } from '@ant-design/icons';
import { resetPassword } from '../../../core/user/userApi';
import './resetPasswordForm.scss';
import { FinishedFailedProps, valuesProps, ResetPasswordFormProps } from './types';
import { AppDispatch, CombinedStates } from '../../../core/types';
import { checkPassword, isPasswordValid, PASSWORD_REQUIREMENTS } from '../../../core/utils/passwordPolicy';

const ResetPasswordForm:FC<ResetPasswordFormProps> = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigation = useNavigate();
    const [password, setPassword] = useState('');
    const {
        isFetching,
    } = useSelector((state:CombinedStates) => state.user);
    const { token, email } = useParams();
    const [form] = Form.useForm();

    const passwordStatus = checkPassword(password);

    const onFinish = (values: valuesProps) => {
        dispatch(resetPassword({
            email,
            password: values.newPassword,
            passwordConfirmation: values.confirmPassword,
            token,
        }));
    };

    const renderIcon = (visible:boolean) => (visible ? <EyeOutlined style={{ color: '#20639B', fontSize: '20px' }} /> : <EyeInvisibleOutlined style={{ color: '#20639B', fontSize: '20px' }} />);

    const onFinishFailed = ({ errorFields }:FinishedFailedProps) => {
        form.scrollToField(errorFields[0]?.name);
    };

    return (

        <div className="login-form-box">
            <div className="form">
                <h3 className="form-title h-1">Reset Password</h3>
                <p className="sub-title">Create a new password below to access to your account</p>
                <Form onFinish={onFinish} onFinishFailed={onFinishFailed} layout="vertical">
                    <Form.Item label="New Password"
                        name="newPassword"
                        rules={[
                            { required: true, message: 'Password is required' },
                            () => ({
                                validator(_, value) {
                                    setPassword(value || '');
                                    if (!value || isPasswordValid(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Password does not meet requirements'));
                                },
                            }),
                        ]}>
                        <Input.Password iconRender={renderIcon} />
                    </Form.Item>
                    <div className="container-one">
                        <h3 className="password-requirements">Password must:</h3>
                        <ul className="password-criteria">
                            {PASSWORD_REQUIREMENTS.map((requirement) => (
                                <li key={requirement.key} style={{ color: passwordStatus[requirement.key] ? 'green' : 'black' }}>
                                    <CheckCircleFilled /> {requirement.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Form.Item label="Verify New Password"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                                },
                            }),
                        ]}>
                        <Input.Password iconRender={renderIcon} />
                    </Form.Item>
                    <Row>
                        <Col xs={24}>
                            <Button size="large" block htmlType="submit" className="save-btn" loading={isFetching}>
                                Save Password
                            </Button>
                            <Button onClick={() => navigation('/')} type="link" className="button-link-one">
                                Back to login
                            </Button>
                        </Col>
                    </Row>

                </Form>
            </div>
        </div>

    );
};

export default ResetPasswordForm;
